/**
 * KAN-912: подтверждение удаления нашего сообщения переписки.
 * can_revoke: "Удалить у клиента и в истории" (основное) и "Только из
 * истории"; иначе пояснение "У клиента удалить нельзя: <причина>" и
 * "Удалить из истории". Запрос, toast и обновление ленты в useDeleteClientMessage.
 */
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeleteClientMessage } from '@/hooks/useDeleteClientMessage';
import type { EstimateMessage } from '@/types/estimate';

interface DeleteMessageDialogProps {
  clientId: string;
  /** Сообщение к удалению; null: диалог закрыт. */
  message: EstimateMessage | null;
  onClose: () => void;
}

function revokeUnavailableText(reason: string | null | undefined): string {
  const text = (reason ?? '').trim();
  return text ? `У клиента удалить нельзя: ${text}` : 'У клиента удалить нельзя';
}

export function DeleteMessageDialog({ clientId, message, onClose }: DeleteMessageDialogProps) {
  const { remove, isPending } = useDeleteClientMessage(clientId, onClose);
  if (!message) return null;
  const canRevoke = message.can_revoke === true;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
    >
      <DialogContent data-testid="delete-message-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Удалить сообщение?</DialogTitle>
          <DialogDescription className="line-clamp-4 whitespace-pre-wrap break-words text-gray-700">
            {message.content || 'Сообщение без текста'}
          </DialogDescription>
        </DialogHeader>
        {!canRevoke && (
          <p className="text-sm text-amber-700">{revokeUnavailableText(message.revoke_unavailable_reason)}</p>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Отмена
          </Button>
          {canRevoke ? (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => remove(message.id, 'history')}
              >
                Только из истории
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isPending}
                onClick={() => remove(message.id, 'client')}
              >
                Удалить у клиента и в истории
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="destructive"
              disabled={isPending}
              onClick={() => remove(message.id, 'history')}
            >
              Удалить из истории
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
